import type { CompanyRedirect } from "@/modules/companies/api/endpoints/company"
import type { Company } from "@/modules/companies/api/types"
import type { I18nLocale } from "@/modules/i18n/types"

import { useQuery } from "@tanstack/react-query"
import { useParams, useSearchParams } from "next/navigation"
import { useContext } from "react"

import {
  DEFAULT_QUERY_CACHE_TIME as QUERY_CACHE_TIME,
  DEFAULT_QUERY_STALE_TIME as QUERY_STALE_TIME,
} from "@/modules/api/constants"
import { COMPANY_QUERY_KEY, getCompany } from "@/modules/companies/api/endpoints/company"
import { useLocale } from "@/modules/locales/useLocale"
import { useRouter } from "@/modules/routing/hooks/useRouter"
import { ServerContext } from "@/modules/server/context"

export type CompanyFetch = {
  dataCompany?: Company
  isErrorCompany: boolean
  isLoadingCompany: boolean
  isSuccessCompany: boolean
}
export const useCompanyFetch = (companySlug?: string): CompanyFetch => {
  const { replace } = useRouter()
  const { companySlug: companySlugParam } = useParams()
  const searchParams = useSearchParams()
  const permalink = companySlug || companySlugParam
  const currentLocale = useLocale()
  const previewLocale = searchParams.get("preview_url")

  const context = useContext(ServerContext) || { hostname: "" }

  if (!context || !context?.hostname) {
    throw new Error(`Missing hostname when retrieving company data.`)
  }

  const { hostname } = context
  const [schoolPermalink] = hostname.split(".")
  const locale = (previewLocale as I18nLocale) || currentLocale

  const {
    data,
    isError: isErrorCompany,
    isPending: isLoadingCompany,
    isSuccess: isSuccessCompany,
  } = useQuery({
    enabled: !!permalink,
    gcTime: QUERY_CACHE_TIME,
    queryFn: () => getCompany({ locale, permalink: permalink as string, preview: !!previewLocale, schoolPermalink }),
    queryKey: [COMPANY_QUERY_KEY, permalink, schoolPermalink, locale, !!previewLocale],
    staleTime: QUERY_STALE_TIME,
  })

  if (data && (data as CompanyRedirect).responseType === "redirect") {
    replace(`/${locale}/companies/${(data as CompanyRedirect).newPermalink}`)
  }

  return {
    dataCompany: data ? (data as Company) : undefined,
    isErrorCompany,
    isLoadingCompany,
    isSuccessCompany,
  }
}
